import { Box, CircularProgress, Stack, styled, Typography } from '@mui/material'

import { FC, useMemo } from 'react'

const RingLabel = styled(Typography)(({ theme }) => ({
  bottom: theme.spacing(-0.5),
  left: `calc(50% + ${theme.spacing(2)})`,
  position: 'absolute',
  whiteSpace: 'nowrap',
  zIndex: 2,
}))

const RingTransformer = styled(Box)(() => ({
  position: 'relative',
  transform: 'rotate(-180deg)',
  zIndex: 1,
}))

const Ring = styled(CircularProgress)(() => ({
  '& circle': {
    strokeLinecap: 'round',
  },
}))

type TrailSystemStatusRingProps = {
  color: string
  size: number
  value: number
  total: number
  label: string
  max: number
  scale?: number
}

const PERCENT_LIMITER = 86.2

export const TrailSystemStatusRing: FC<TrailSystemStatusRingProps> = (props) => {
  const value = useMemo(() => {
    if (!props.total || !props.max) return 0
    const result = (props.value / props.total) * PERCENT_LIMITER * (PERCENT_LIMITER / ((props.max / props.total) * 100))
    return Math.ceil(result)
  }, [props.total, props.value, props.max])

  const backgroundValue = useMemo(() => {
    if (!props.total || !props.max) return 0
    const result = (props.max / props.total) * PERCENT_LIMITER * (PERCENT_LIMITER / ((props.max / props.total) * 100))
    return Math.ceil(result)
  }, [props.total, props.max])

  const scale = useMemo(() => props.scale || 1, [props.scale])

  return (
    <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
      <Stack alignItems="center" justifyContent="center" position="relative" width="100%">
        <RingTransformer>
          <Ring
            variant="determinate"
            value={backgroundValue}
            size={props.size * scale}
            thickness={500 / props.size}
            sx={{ color: '#555555', left: 0, position: 'absolute', top: 0 }}
          />
          <Ring
            variant="determinate"
            value={value || 0}
            size={props.size * scale}
            thickness={500 / props.size}
            sx={{ color: props.color }}
          />
        </RingTransformer>
        <RingLabel variant="body2" fontSize={13 * scale}>
          <span>
            {props.value} {props.label}
          </span>
        </RingLabel>
      </Stack>
    </Stack>
  )
}
TrailSystemStatusRing.displayName = 'TrailSystemStatusRing'
